import React from 'react';
import { Calculator, Truck, Clock, Ruler, DollarSign, PiggyBank, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета объема бетона
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Округление результатов для красивого отображения
  const roundedVolume = Math.round(results.totalVolume * 100) / 100;

  return (
    <ToolResult title="Результаты расчета объема бетона">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Объем бетона */}
        <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-900/30 dark:to-sky-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
          <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
            <Ruler className="w-4 h-4"/>
            Объем бетона
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-cyan-800 dark:text-cyan-200" aria-live="polite">
              {roundedVolume} м³
            </span>
          </div>
          <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1">
            {results.baseVolume.toFixed(2)} м³ + {results.wastageVolume.toFixed(2)} м³ запас
          </p>
        </div>

        {/* Стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Общая стоимость
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatCurrency(results.totalCost, 0)}
            </span>
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              ({formatCurrency(results.pricePerCubicMeter, 0)} за м³)
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Включает стоимость материала{results.deliveryCost > 0 ? ', доставки' : ''}{results.pumpCost > 0 ? ', насоса' : ''}{results.laborCost > 0 ? ' и работы' : ''}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Тип конструкции" 
          value={results.projectType || 'Не указан'} 
          color="cyan" 
          icon={<Ruler className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Марка бетона" 
          value={results.concreteGrade || 'Не указана'} 
          color="sky" 
          icon={<Calculator className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Базовый объем бетона" 
          value={`${results.baseVolume.toFixed(2)} м³`} 
          icon={<Ruler className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Запас на отходы" 
          value={`${results.wastageVolume.toFixed(2)} м³`} 
          color="amber"
          icon={<Ruler className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Итоговый объем бетона" 
          value={`${results.totalVolume.toFixed(2)} м³`} 
          color="green"
          icon={<Ruler className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Вес бетона" 
          value={`${Math.round(results.concreteWeight)} кг (${(results.concreteWeight / 1000).toFixed(1)} т)`} 
          icon={<Ruler className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Стоимость бетона" 
          value={formatCurrency(results.concreteCost, 0)} 
          color="green" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        {results.deliveryCost > 0 && (
          <ResultItem 
            label="Стоимость доставки" 
            value={formatCurrency(results.deliveryCost, 0)} 
            color="blue" 
            icon={<Truck className="w-4 h-4"/>}
          />
        )}
        
        {results.pumpCost > 0 && (
          <ResultItem 
            label="Стоимость насоса" 
            value={formatCurrency(results.pumpCost, 0)} 
            color="orange" 
            icon={<Truck className="w-4 h-4"/>}
          />
        )}
        
        {results.laborCost > 0 && (
          <ResultItem 
            label="Стоимость работ" 
            value={formatCurrency(results.laborCost, 0)} 
            color="purple" 
            icon={<DollarSign className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Общие затраты" 
          value={formatCurrency(results.totalCost, 0)} 
          color="green" 
          icon={<PiggyBank className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Количество необходимых машин" 
          value={`${results.trucksNeeded} шт.`} 
          icon={<Truck className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Примерное время заливки" 
          value={`${results.totalPourTimeMinutes} мин.`} 
          icon={<Clock className="w-4 h-4"/>}
        />

        {/* Блок рекомендаций */}
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 mt-2 border border-blue-200 dark:border-blue-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Рекомендации по заливке</h4>
              <ul className="space-y-2">
                {results.recommendations.map((recommendation, index) => (
                  <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;